import { Component, Input, QueryList, ViewChildren } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
  Allocation,
  AllocationConseil,
  AllocationTransfertOption,
  AllocationVersementOption,
  FicheConseil,
  HorizonPlacementEnum,
  NiveauDeRisque,
  PortefeuilleOption,
  StorageFile,
  VpModeOptions,
} from '@lib/models/generated/graphql';
import { NatureOperationId } from '@lib/models/graphqlData';
import { DocumentsService } from '@lib/services/documents.service';
import { OuiButtonToggleOption } from 'omnium-ui/button-toggle';
import { OuiSelectOption, OuiValidationError } from 'omnium-ui/form-field';
import { Subscription } from 'rxjs';
import { AbstractStepComponent } from 'src/app/components/declare-fiche-conseil/steps/abstract-step.component';
import { AllocationConsultantFileUploadComponent } from 'src/app/components/declare-fiche-conseil/steps/projet-investissement-step/allocation-asset/allocation-consultant-file-upload/allocation-consultant-file-upload.component';
import { AllocationService, HorizonRetraite, PatrimoineWarning } from 'src/app/services/allocation.service';
import { FicheConseilService } from 'src/app/services/fiche-conseil.service';
import { FormConfigService } from 'src/app/services/form-config.service';
import { FieldFormControl } from 'src/fieldFormControl/FieldFormControl';

const WithOptimizAnexe = 1;
const NoOptimizAnexe = 0;
@Component({
  selector: 'app-allocation-asset',
  templateUrl: './allocation-asset.view.html',
  styleUrls: ['./allocation-asset.component.scss'],
})
export class AllocationAssetComponent extends AbstractStepComponent {
  protected readonly vp_allocation_souscription_text =
    "L'allocation des versements programmés et du versement initial sont :";
  protected readonly vp_allocation_versementComplementaire_text =
    "L'allocation des versements programmés et de l'allocation cible de mon contrat après le versement complémentaire sont :";
  @ViewChildren(AllocationConsultantFileUploadComponent)
  consultantFileUploader: QueryList<AllocationConsultantFileUploadComponent>;

  readonly requiredValueError: OuiValidationError = {
    code: 'required',
    message: 'Ce champs est requis !',
  };

  @Input() avecMiseEnPlaceOuModificationVP: FieldFormControl<boolean, boolean>;
  @Input() vpMode: VpModeOptions | null = null;
  @Input()
  set avecChangementAllocation(value: boolean | null) {
    this._avecChangementAllocation = value;

    if ((value === false || value === null) && this.isAllocationIdenticalControl) {
      this.isAllocationIdenticalControl.setValue(null, { emitEvent: false });
    }
  }

  get avecChangementAllocation(): boolean | null {
    return this._avecChangementAllocation;
  }

  private _avecChangementAllocation: boolean | null = null;
  risqueOptions: OuiSelectOption<NiveauDeRisque>[] = [];

  viRisqueControl: FieldFormControl<NiveauDeRisque | null, NiveauDeRisque>;
  vpRisqueControl: FieldFormControl<NiveauDeRisque | null, NiveauDeRisque> | undefined;

  horizonPlacement = HorizonPlacementEnum;
  horizonRetraite = HorizonRetraite;
  patrimoineWarning = PatrimoineWarning;
  niveauRisque = NiveauDeRisque;

  fileControl = new FormControl<File[]>([]);
  errors: OuiValidationError[] = [];

  isGestionLibre = false;

  allocationPartenaireControl: FieldFormControl<AllocationConseil | null, AllocationConseil>;
  allocationPartenaireOptions: OuiSelectOption<AllocationConseil>[] = [];
  allocationProduitSub: Subscription | undefined;
  allocationPartenaireSub: Subscription | undefined;

  availableAllocations: Allocation[] = [];
  partenaireFile: File | undefined;
  partenaireStorageFile: StorageFile | undefined;
  showLibreFileError: boolean = false;

  optionnalAnnexeTogglecontrol: FieldFormControl<number, PortefeuilleOption>;

  optionnalAnnexeToggleOptions: OuiButtonToggleOption<number>[] = [
    { value: WithOptimizAnexe, label: 'Annexe Optimiz' },
    { value: NoOptimizAnexe, label: 'Zone libre' },
  ];

  isAllocationIdenticalControl: FieldFormControl<AllocationVersementOption | null>;
  isAllocationIdenticalOptions: OuiButtonToggleOption<AllocationVersementOption>[] = [
    { value: AllocationVersementOption.Identique, label: 'Strictement identiques' },
    { value: AllocationVersementOption.Differente, label: 'Différentes' },
  ];

  isTransfertIdenticalControl: FieldFormControl<AllocationTransfertOption | null>;
  isTransfertIdenticalOptions: OuiButtonToggleOption<AllocationTransfertOption>[] = [
    { value: AllocationTransfertOption.Identique, label: 'Strictement identiques' },
    { value: AllocationTransfertOption.Differente, label: 'Différentes' },
  ];

  portefeuilleDetailsControl: FieldFormControl<string | null, string | null>;
  arbitrageLibreSupportsSortantsControl: FieldFormControl<string | null, string | null>;
  arbitrageLibreSupportsEntrantsControl: FieldFormControl<string | null, string | null>;
  previousModeGestion: string | undefined;

  labelProfilsRisqueVC: string | undefined;
  labelSubtitleVC: string | undefined;
  textAnnexeOptimiz: string | undefined;
  textZoneLibre: string | undefined;
  vpAllocationText: string | undefined;
  dualZonePrimaryLabel: string | undefined;
  dualZoneSecondaryLabel: string | undefined;
  constructor(
    protected override ficheConseilService: FicheConseilService,
    protected formConfigService: FormConfigService,
    protected allocationService: AllocationService,
    private documentService: DocumentsService
  ) {
    super(ficheConseilService);
    this.arbitrageLibreSupportsSortantsControl = new FieldFormControl<string | null, string | null>({
      formConfigService: this.formConfigService,
      ficheConseilService: this.ficheConseilService,
      dataKey: 'arbitrageLibreSupportsSortants',
      defaultValue: this.currentFicheConseil?.arbitrageLibreSupportsSortants ?? null,
      options: {
        validators: [Validators.required],
      },
    });
    this.arbitrageLibreSupportsEntrantsControl = new FieldFormControl<string | null, string | null>({
      formConfigService: this.formConfigService,
      ficheConseilService: this.ficheConseilService,
      dataKey: 'arbitrageLibreSupportsEntrants',
      defaultValue: this.currentFicheConseil?.arbitrageLibreSupportsEntrants ?? null,
      options: {
        validators: [Validators.required],
      },
    });
  }

  public override beforeFicheConseilUpdate(
    newFicheConseil: FicheConseil | null,
    previousFicheConseil: FicheConseil | null
  ): void {
    if (newFicheConseil && this.hasDataChanged(newFicheConseil, previousFicheConseil)) {
      this.initAndSetUp(newFicheConseil);
    }
  }
  public override isStepValid(): boolean {
    if (this.isGestionLibre) {
      let versementOK = this.viRisqueControl.value !== null && !this.viRisqueControl.errors;

      const mustSpecifyAllocationOnVP = this.showAllocationVP;

      if (mustSpecifyAllocationOnVP) {
        const alloctionSpecifiedOnVP =
          this.isAllocationIdenticalControl.value !== null && !this.isAllocationIdenticalControl.errors;
        versementOK = versementOK && alloctionSpecifiedOnVP;
      }
      let transfertIdentiqueOK = true;

      if (this.currentFicheConseil?.versements?.souscriptionAvecTransfert) {
        transfertIdentiqueOK =
          this.isTransfertIdenticalControl.value !== null && !this.isTransfertIdenticalControl.errors;
      }

      if (this.currentFicheConseil?.versements.avecVersementProgramme) {
        const versementProgrammeOK = this.vpRisqueControl?.value !== null && !this.vpRisqueControl?.errors;
        versementOK = versementOK && versementProgrammeOK;
      }
      const valid =
        transfertIdentiqueOK &&
        versementOK &&
        ((!this.isGestionLibre && !!this.currentFicheConseil?.allocationConsultantFileId) || //FIXME on verifie isGestionLibre alors qu 'on est dans un if (this.isGestionLibre) ??
          (this.isGestionLibre &&
            ((this.optionnalAnnexeTogglecontrol.value === NoOptimizAnexe &&
              (this.isArbitrageLibre
                ? !!this.arbitrageLibreSupportsSortantsControl.value &&
                  !!this.arbitrageLibreSupportsEntrantsControl.value
                : !!this.portefeuilleDetailsControl.value)) ||
              (this.optionnalAnnexeTogglecontrol.value === WithOptimizAnexe &&
                !!this.currentFicheConseil?.allocationConsultantFileId))));
      return valid;
    }

    return this.allocationPartenaireControl.value !== null && !this.allocationPartenaireControl.errors;
  }
  public override markAsTouched(): void {
    this.optionnalAnnexeTogglecontrol.markAsTouched();
    this.isAllocationIdenticalControl.markAsTouched();
    this.isTransfertIdenticalControl.markAsTouched();
    this.allocationPartenaireControl?.markAllAsTouched();
    this.viRisqueControl.markAsTouched();
    this.vpRisqueControl?.markAsTouched();
    this.showLibreFileError = !(this.isGestionLibre && !!this.currentFicheConseil?.allocationConsultantFileId);
    this.consultantFileUploader.first?.markAsTouched();
    this.portefeuilleDetailsControl.markAsTouched();
    this.arbitrageLibreSupportsSortantsControl.markAsTouched();
    this.arbitrageLibreSupportsEntrantsControl.markAsTouched();
  }
  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.allocationProduitSub?.unsubscribe();
  }
  override ngOnInit(): void {
    super.ngOnInit();
    this.allocationProduitSub = this.allocationService.allocationsByProduitSubject.subscribe(newAllocations => {
      this.availableAllocations = newAllocations;
      if (this.currentFicheConseil) {
        this.initAndSetUp(this.currentFicheConseil);
      }
    });
  }

  get showAllocationVP(): boolean | null | undefined {
    if (this.currentFicheConseil?.natureOperation?.id === NatureOperationId.VersementComplementaire) {
      return (
        this.isGestionLibre &&
        this.avecMiseEnPlaceOuModificationVP &&
        (this.vpMode === VpModeOptions.MiseEnPlace ||
          (this.vpMode === VpModeOptions.Modification && this.avecChangementAllocation))
      );
    } else {
      return this.isGestionLibre && this.currentFicheConseil?.versements?.avecVersementProgramme;
    }
  }

  get isArbitrageLibre(): boolean {
    return this.currentFicheConseil?.natureOperation?.id === NatureOperationId.ArbitrageLibre;
  }

  //check data changed
  hasDataChanged(newFiche: FicheConseil, oldFiche: FicheConseil | null) {
    const previousModeGestion = oldFiche?.modeDeGestion?.code;
    this.previousModeGestion = previousModeGestion;
    if (!newFiche) {
      return false;
    }
    if (!oldFiche) {
      return true;
    }
    return (
      // mode de gestion
      newFiche.modeDeGestion?.code != oldFiche.modeDeGestion?.code ||
      // disponibilité de l'épargne
      newFiche.disponibiliteEpargne != oldFiche.disponibiliteEpargne ||
      // Tolérance au risque du projet
      newFiche.toleranceRisque != oldFiche.toleranceRisque ||
      // horizon placement
      newFiche.horizonPlacement?.value != oldFiche.horizonPlacement?.value ||
      newFiche.horizonPlacement?.horizonRetraite != oldFiche.horizonPlacement?.horizonRetraite ||
      // patrimoine (FIXME this should change with the new patrimoine model)
      // tolerance perte (FIXME this should change with the newprofil data)
      newFiche.investisseur?.profil?.hasTolerancePerte != oldFiche.investisseur?.profil?.hasTolerancePerte ||
      // profil qpi
      newFiche.investisseur?.profil?.profilQpiCode != oldFiche.investisseur?.profil?.profilQpiCode ||
      newFiche.coInvestisseur?.profil?.profilQpiCode != oldFiche.coInvestisseur?.profil?.profilQpiCode ||
      // versement initial
      newFiche.versements?.versementInitial?.montantBrut != oldFiche.versements?.versementInitial?.montantBrut ||
      // versement programme
      newFiche.versements?.avecVersementProgramme != oldFiche.versements?.avecVersementProgramme
    );
  }

  initAndSetUp(fiche: FicheConseil) {
    if (!fiche) return;

    this.labelProfilsRisqueVC = 'Versement initial';
    this.labelSubtitleVC = 'Allocation du versement initial';
    this.textAnnexeOptimiz =
      "J'importe ici mon annexe règlementaire réalisée sur Optimiz Portfolio correspondant au présent projet d'investissement (en présence d'une dynamisation progressive, je dois importer la situation cible du contrat après le dernier arbitrage programmé) :";
    this.textZoneLibre =
      "Pour chaque support, je précise son nom, ET son code ISIN ET son pourcentage ET son risque SRI (en présence d'une dynamisation progressive, je détaille l'Allocation cible après le dernier arbitrage)";
    this.vpAllocationText = this.vp_allocation_souscription_text;
    this.dualZonePrimaryLabel = undefined;
    this.dualZoneSecondaryLabel = undefined;
    if (fiche.natureOperation?.id === NatureOperationId.VersementComplementaire) {
      this.labelProfilsRisqueVC = 'Du contrat après l’opération principale';
      this.labelSubtitleVC = 'Allocation du versement complémentaire';
      this.textAnnexeOptimiz =
        ' J\'importe ici mon "Annexe à la fiche conseil" (et non pas le rapport PDF comparaison)  réalisée sur Optimiz Portfolio dans le cadre de la fonctionnalité "Projeter une opération" correspondant à l\'opération de versement complémentaire :';
      this.textZoneLibre =
        'Pour chaque support de mon versement complémentaire, je précise son nom, ET son code ISIN ET son pourcentage ET son risque SRI';
      this.vpAllocationText = this.vp_allocation_versementComplementaire_text;
    }
    if (fiche.natureOperation?.id === NatureOperationId.ArbitrageLibre) {
      this.labelProfilsRisqueVC = "Du contrat après l'opération principale";
      this.labelSubtitleVC = "Allocation de l'arbitrage";
      this.textAnnexeOptimiz =
        'J\'importe ici mon "Annexe à la fiche conseil" (et non pas le rapport PDF comparaison) realisée sur Optimiz Portfolio dans le cadre de la fonctionnalité "Projeter une opération" correspondant a l\'opération d\' arbitrage libre :';
      this.textZoneLibre = '';
      this.dualZonePrimaryLabel =
        "Préciser <strong>les supports sortants</strong> avec le code ISIN, nom du support et pourcentages ou montant de désinvestissement qui correspondront aux valeurs indiquées sur le bulletin d'arbitrage libre";
      this.dualZoneSecondaryLabel =
        "Préciser <strong>les supports entrants</strong> avec le code ISIN, nom du support, risque SRI et pourcentages ou montant d'investissement qui correspondront aux valeurs indiquées sur le bulletin d'arbitrage libre";
    }

    if (fiche.natureOperation?.id === NatureOperationId.ChangementModeGestion) {
      this.labelProfilsRisqueVC = "Du contrat après l'opération principale";
      this.labelSubtitleVC = "Allocation de l'arbitrage";
      this.textAnnexeOptimiz =
        'J\'importe ici mon "Annexe à la fiche conseil" présentant la nouvelle allocation cible de mon contrat avec le passage en gestion libre :';
      this.textZoneLibre =
        'Pour chaque support de ma nouvelle allocation en gestion libre, je précise son nom, ET son code ISIN, ET son pourcentage ET son risque SRI :';
    }

    this.allocationService.computeAllocationRule(fiche);
    this.risqueOptions = this.allocationService.generateNiveauDeRisqueOptions();
    this.isGestionLibre = fiche.modeDeGestion?.code === 'LIBRE';
    // mode gestion libre
    if (this.isGestionLibre) {
      this.allocationPartenaireSub?.unsubscribe();
      this.initGestionLibreControls(fiche);
    }

    const wasGestionLibreOrNull = this.previousModeGestion === 'LIBRE' || !this.previousModeGestion;
    // allocation partenaire
    if (!this.isGestionLibre) {
      this.allocationPartenaireOptions = this.allocationService.buildAllocationPartenairesOptions(fiche);
      fiche.allocationVersementOption = null;
    }
    // if we switched from gestion libre to another mode, we need to init the contoller for allocation partenaire
    // and reset the value of allocation vesement option
    if (wasGestionLibreOrNull) {
      this.initAllocationPartenaireControls();
    } else {
      // we reset the value of the allocation partenaire control
      this.allocationPartenaireControl.setValue(null);
      this.allocationPartenaireControl.reset();
    }
    this.checkControlValueNiveauDeRisque(fiche);
  }

  initGestionLibreControls(fiche: FicheConseil) {
    this.viRisqueControl = new FieldFormControl<NiveauDeRisque | null, NiveauDeRisque>({
      dataKey: 'versements.versementInitial.niveauDeRisque',
      formConfigService: this.formConfigService,
      ficheConseilService: this.ficheConseilService,
      defaultValue: null,
      options: {
        validators: [Validators.required],
      },
    });
    // versement programmés
    if (fiche.versements?.avecVersementProgramme) {
      this.vpRisqueControl = new FieldFormControl<NiveauDeRisque | null, NiveauDeRisque>({
        dataKey: 'versements.versementProgramme.versement.niveauDeRisque',
        formConfigService: this.formConfigService,
        ficheConseilService: this.ficheConseilService,
        defaultValue: null,
        options: {
          validators: [Validators.required],
        },
      });
    } else {
      this.vpRisqueControl = undefined;
    }

    this.portefeuilleDetailsControl = new FieldFormControl<string | null, string | null>({
      formConfigService: this.formConfigService,
      ficheConseilService: this.ficheConseilService,
      dataKey: 'allocationPrecision',
      defaultValue: null,
      options: {
        validators: [Validators.required],
      },
    });
    this.optionnalAnnexeTogglecontrol = new FieldFormControl<number, PortefeuilleOption>({
      formConfigService: this.formConfigService,
      ficheConseilService: this.ficheConseilService,
      ficheDataToFieldDataWrapper: (ficheData: PortefeuilleOption) =>
        ficheData === PortefeuilleOption.SansAnnexe ? 0 : 1,
      fieldDataToFicheDataWrapper: (fieldData: number) =>
        fieldData === 0 ? PortefeuilleOption.SansAnnexe : PortefeuilleOption.AvecAnnexe,
      dataKey: 'portefeuilleOption',
      defaultValue: WithOptimizAnexe,
      options: {
        nonNullable: true,
      },
    });

    /**
     * we need to set the default value of the portefeuilleOption to AvecAnnexe if it is null
     * because if ther is no update on the fiche, the default value will be null and the portefeuilleOption won't be set in the pdf
     */
    if (this.currentFicheConseil) {
      if (this.currentFicheConseil?.portefeuilleOption == null) {
        this.currentFicheConseil.portefeuilleOption = PortefeuilleOption.AvecAnnexe;
      }
    }

    this.isAllocationIdenticalControl = new FieldFormControl<AllocationVersementOption | null>({
      formConfigService: this.formConfigService,
      ficheConseilService: this.ficheConseilService,
      dataKey: 'allocationVersementOption',
      defaultValue: null,
      options: {
        validators: [Validators.required],
      },
    });

    this.isTransfertIdenticalControl = new FieldFormControl<AllocationTransfertOption | null>({
      formConfigService: this.formConfigService,
      ficheConseilService: this.ficheConseilService,
      dataKey: 'allocationTransfertOption',
      defaultValue: null,
      options: {
        validators: [Validators.required],
      },
    });
  }

  initAllocationPartenaireControls() {
    this.allocationPartenaireControl = new FieldFormControl<AllocationConseil | null, AllocationConseil>({
      dataKey: 'allocation',
      formConfigService: this.formConfigService,
      ficheConseilService: this.ficheConseilService,
      ficheDataToFieldDataWrapper: (ficheData: Allocation | null) => {
        if (ficheData) {
          return this.allocationPartenaireOptions.find(allopt => allopt.value?.id === ficheData.id)?.value ?? null;
        }
        return null;
      },
      defaultValue: null,
      options: {
        validators: [Validators.required],
      },
    });

    this.allocationPartenaireSub = this.allocationPartenaireControl.valueChanges.subscribe(allocation => {
      if (!allocation) {
        this.partenaireStorageFile = undefined;
        this.partenaireFile = undefined;
        return;
      }
      const alloc = this.availableAllocations.find(alloc => alloc.id === allocation?.id);
      if (alloc?.storageFile) {
        this.partenaireStorageFile = alloc.storageFile;
        this.partenaireFile = new File([], this.partenaireStorageFile.fileName, {
          type: this.partenaireStorageFile.fileType,
        });
      }
      if (this.partenaireFile && this.currentFicheConseil) {
        this.currentFicheConseil.portefeuilleOption = PortefeuilleOption.AvecAnnexe;
      }
    });
  }
  onPartenaireFileClick() {
    if (this.partenaireStorageFile) {
      this.documentService.displayStorageFileInNewTab(this.partenaireStorageFile);
    }
  }

  /**
   * Checks and resets the niveau de risque controls for the versement initial and programme.
   *
   * This method verifies if the niveau de risque values for the versement initial and versement
   * programme are present in the set of disabled profile risques. If a match is found, the
   * corresponding profile control value is reset to null.
   *
   * @param fiche - The FicheConseil object containing the versement details to be validated.
   */
  checkControlValueNiveauDeRisque(fiche: FicheConseil) {
    if (this.isGestionLibre) {
      const viNiveauDeRisque = fiche.versements?.versementInitial?.niveauDeRisque;
      const vpNiveauDeRisque = fiche.versements?.versementProgramme?.versement?.niveauDeRisque;
      if (
        this.viRisqueControl &&
        viNiveauDeRisque &&
        this.allocationService.allocationRule.disabledNiveauDeRisque.has(viNiveauDeRisque)
      ) {
        this.viRisqueControl?.setValue(null);

      }
      if (
        this.vpRisqueControl &&
        vpNiveauDeRisque &&
        this.allocationService.allocationRule.disabledNiveauDeRisque.has(vpNiveauDeRisque)
      ) {
        this.vpRisqueControl?.setValue(null);
      }
    }

    if (!this.isGestionLibre) {
      const niveauDeRisque = fiche.allocation?.niveauDeRisque;
      if (niveauDeRisque && this.allocationService.allocationRule.disabledNiveauDeRisque.has(niveauDeRisque)) {
        this.allocationPartenaireControl?.setValue(null);
      }
    }
  }
}
